import { useId, useState } from "react";

/**
 * Format a numeric value with commas (e.g. 1000 → "1,000")
 * Preserves decimals and partial input like "1,234." while typing.
 */
function formatWithCommas(val) {
  if (val === "" || val == null) return "";
  const str = String(val);
  // Split on decimal point so we only format the integer part
  const [intPart, decPart] = str.split(".");
  const formatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return decPart !== undefined ? `${formatted}.${decPart}` : formatted;
}

function stripCommas(str) {
  return String(str).replace(/,/g, "");
}

export default function FormField({
  label,
  name,
  type = "text",
  placeholder,
  value,
  onChange,
  onBlur,
  required = false,
  error,
  showError = true,
  rightIcon,
  register,
  rules,
  disabled = false,
  className,
  ...rest // ✅ allow min/max/step/etc (className is merged below — do not spread onto input)
}) {
  const id = useId();
  const [showPassword, setShowPassword] = useState(false);

  const isNumber = type === "number";

  const inputProps = register
    ? { ...register(name, rules) }
    : {
        value: isNumber ? formatWithCommas(value) : value,
        onChange: isNumber
          ? (e) => {
              const raw = stripCommas(e.target.value);
              // Allow empty, digits, one decimal point, and leading minus
              if (raw !== "" && !/^-?\d*\.?\d*$/.test(raw)) return;
              const syntheticEvent = { ...e, target: { ...e.target, value: raw } };
              if (onChange) onChange(syntheticEvent);
            }
          : onChange,
        onBlur,
      };

  const inputType = type === "password" && showPassword ? "text" : isNumber ? "text" : type;
  const showPasswordToggle = type === "password";

  // Default step="any" for number inputs so decimals are accepted
  if (isNumber && rest.step === undefined) {
    rest.step = "any";
  }

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium">
        {label} {required && <span className="text-smiles-red">*</span>}
      </label>

      <div className="mt-2 relative">
        <input
          id={id}
          type={inputType}
          inputMode={isNumber ? "decimal" : undefined}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          className={[
            "w-full rounded-xl border px-4 py-3 pr-10 text-palette-gray-primary outline-none transition-colors",
            disabled
              ? "cursor-not-allowed border-palette-gray-border bg-palette-gray-subtle text-palette-gray-text"
              : "border-palette-gray-border bg-white shadow-sm hover:border-palette-gray-placeholder focus:border-palette-indigo-label focus:ring-2 focus:ring-palette-indigo-label/30",
            isNumber
              ? "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              : "",
            className,
          ]
            .filter(Boolean)
            .join(" ")}
          {...inputProps}
          {...rest} // ✅ keep this last so min/max/step etc always apply
        />

        {showPasswordToggle ? (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-smiles-lightGray hover:text-smiles-gray"
          >
            {showPassword ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-5 h-5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
                />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-5 h-5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
            )}
          </button>
        ) : (
          rightIcon && <div className="absolute right-3 top-1/2 -translate-y-1/2">{rightIcon}</div>
        )}
      </div>

      {showError && error && <p className="mt-1 text-xs text-palette-rose-badge">{error}</p>}
    </div>
  );
}
